import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const cp=x=>x==null?x:JSON.parse(JSON.stringify(x));

function create(opts={}){
  const gateway=opts.gateway||g.ZooInvestRuntimeDataContractGateway;
  if(!gateway||typeof gateway.gate!=='function') throw Error('DATA_GATEWAY_REQUIRED');

  const providers=new Map();

  function register(name,adapter){
    if(!name||typeof name!=='string') throw Error('PROVIDER_NAME_REQUIRED');
    if(!adapter||typeof adapter.fetch!=='function') throw Error('PROVIDER_FETCH_REQUIRED');
    providers.set(name,Object.freeze({
      name,
      datasets:Array.isArray(adapter.datasets)?Object.freeze([...adapter.datasets]):Object.freeze([]),
      fetch:adapter.fetch
    }));
    return true;
  }

  function unregister(name){
    return providers.delete(name);
  }

  function listProviders(){
    return Object.freeze([...providers.values()].map(p=>Object.freeze({
      name:p.name,
      datasets:Object.freeze([...p.datasets])
    })));
  }

  async function ingest(request={}){
    const providerName=request.provider;
    const dataset=request.dataset;
    const adapter=providers.get(providerName);

    if(!adapter){
      return gateway.gate({
        provider:providerName||null,
        dataset:dataset||null,
        symbol:request.symbol||null,
        state:'ERROR',
        error:'PROVIDER_NOT_REGISTERED',
        provenance:{source:'provider-registry'}
      });
    }

    if(adapter.datasets.length && !adapter.datasets.includes(dataset)){
      return gateway.gate({
        provider:providerName,
        dataset,
        symbol:request.symbol||null,
        state:'ERROR',
        error:'DATASET_NOT_SUPPORTED',
        provenance:{source:'provider-registry'}
      });
    }

    try{
      const raw=await adapter.fetch(cp(request));
      const envelope={
        provider:providerName,
        dataset,
        symbol:request.symbol||raw?.symbol||null,
        asOf:raw?.asOf||null,
        receivedAt:raw?.receivedAt||new Date().toISOString(),
        state:raw?.state||'UNVERIFIED',
        provenance:cp(raw?.provenance||{}),
        payload:raw?.payload===undefined?null:cp(raw.payload),
        error:raw?.error||null
      };
      return gateway.gate(envelope);
    }catch(e){
      return gateway.gate({
        provider:providerName,
        dataset,
        symbol:request.symbol||null,
        receivedAt:new Date().toISOString(),
        state:'ERROR',
        provenance:{source:'adapter-exception'},
        error:String(e&&e.message||e)
      });
    }
  }

  async function ingestMany(requests=[]){
    const results=[];
    for(const r of Array.isArray(requests)?requests:[]){
      results.push(await ingest(r));
    }
    return gateway.combine(results.map(x=>x.envelope));
  }

  return Object.freeze({VERSION,register,unregister,listProviders,ingest,ingestMany});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const gateway={
    gate:e=>{
      const issues=[];
      if(!e.provider) issues.push('PROVIDER_REQUIRED');
      if(!e.dataset) issues.push('DATASET_REQUIRED');
      if(e.state==='VERIFIED'&&!e.asOf) issues.push('VERIFIED_REQUIRES_ASOF');
      if(e.state==='VERIFIED'&&(!e.provenance||!Object.keys(e.provenance).length)) issues.push('VERIFIED_REQUIRES_PROVENANCE');
      if(issues.length) return {status:'BLOCK',envelope:e,issues,usable:false};
      if(e.state==='ERROR'||e.state==='MISSING') return {status:'REVIEW',envelope:e,issues:[],usable:false};
      if(e.state==='STALE'||e.state==='UNVERIFIED') return {status:'REVIEW',envelope:e,issues:[],usable:true};
      return {status:'READY',envelope:e,issues:[],usable:true};
    },
    combine:envs=>{
      const rs=envs.map(e=>gateway.gate(e));
      return {
        status:rs.some(x=>x.status==='BLOCK')?'BLOCK':rs.some(x=>x.status==='REVIEW')?'REVIEW':'READY',
        readyCount:rs.filter(x=>x.status==='READY').length,
        reviewCount:rs.filter(x=>x.status==='REVIEW').length,
        blockCount:rs.filter(x=>x.status==='BLOCK').length,
        results:rs
      };
    }
  };

  const b=create({gateway});
  b.register('SEC',{
    datasets:['fundamentals'],
    fetch:async req=>({
      symbol:req.symbol,
      asOf:'2026-08-30',
      state:'VERIFIED',
      provenance:{source:'sec-test'},
      payload:{revenue:100}
    })
  });

  ok('provider registered',b.listProviders().length===1);
  ok('dataset advertised',b.listProviders()[0].datasets.includes('fundamentals'));

  const r=await b.ingest({provider:'SEC',dataset:'fundamentals',symbol:'AAA'});
  ok('verified provider result ready',r.status==='READY');
  ok('symbol preserved',r.envelope.symbol==='AAA');
  ok('payload preserved',r.envelope.payload.revenue===100);

  const unsupported=await b.ingest({provider:'SEC',dataset:'prices',symbol:'AAA'});
  ok('unsupported dataset reviews',unsupported.status==='REVIEW');
  ok('unsupported dataset reason preserved',unsupported.envelope.error==='DATASET_NOT_SUPPORTED');

  const missing=await b.ingest({provider:'NOPE',dataset:'x',symbol:'AAA'});
  ok('missing provider reviews',missing.status==='REVIEW');
  ok('missing provider reason preserved',missing.envelope.error==='PROVIDER_NOT_REGISTERED');

  b.register('BROKEN',{fetch:async()=>{throw Error('TIMEOUT')}});
  const br=await b.ingest({provider:'BROKEN',dataset:'x',symbol:'AAA'});
  ok('adapter exception contained',br.status==='REVIEW');
  ok('adapter exception preserved',br.envelope.error==='TIMEOUT');

  b.register('UNVERIFIED',{datasets:['news'],fetch:async()=>({
    state:'UNVERIFIED',
    provenance:{source:'headline-feed'},
    payload:{headline:'x'}
  })});
  const uv=await b.ingest({provider:'UNVERIFIED',dataset:'news',symbol:'AAA'});
  ok('unverified provider cannot become ready',uv.status==='REVIEW');
  ok('unverified payload remains visible',uv.usable===true);

  const many=await b.ingestMany([
    {provider:'SEC',dataset:'fundamentals',symbol:'AAA'},
    {provider:'UNVERIFIED',dataset:'news',symbol:'AAA'}
  ]);
  ok('mixed batch becomes review',many.status==='REVIEW');
  ok('batch counts verified result',many.readyCount===1);

  ok('unregister works',b.unregister('BROKEN')===true);
  ok('unregistered provider removed',!b.listProviders().some(x=>x.name==='BROKEN'));

  let nameErr=false;try{b.register('',{fetch:async()=>({})})}catch(e){nameErr=e.message==='PROVIDER_NAME_REQUIRED'}
  ok('provider name required',nameErr);

  let fetchErr=false;try{b.register('X',{})}catch(e){fetchErr=e.message==='PROVIDER_FETCH_REQUIRED'}
  ok('provider fetch required',fetchErr);

  let gatewayErr=false;try{create({gateway:{}})}catch(e){gatewayErr=e.message==='DATA_GATEWAY_REQUIRED'}
  ok('gateway required',gatewayErr);

  const api=create({gateway});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestRuntimeProviderIngestionBridge=Object.freeze({VERSION,create,selfTest});
})(engineContext);